/**
 * Calendar Module - Full Page View
 * Google Calendar / Outlook-styled calendar with full CRUD functionality
 * Features: Month/Week/Day/List views with proper calendar grids
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api';
import { useModuleRefresh } from '@/contexts/ModuleRefreshContext';

interface CalendarEvent {
  id: number;
  title: string;
  description: string;
  start_time: string;
  end_time: string;
  location?: string;
  attendees?: string[];
  category: string;
  created_at: string;
  updated_at: string;
}

type ViewMode = 'month' | 'week' | 'day' | 'list';

const CalendarModule: React.FC = () => {
  const navigate = useNavigate();
  const { registerCalendarRefresh } = useModuleRefresh();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState<{ date: Date; events: CalendarEvent[] } | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDayEventsModal, setShowDayEventsModal] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(false);

  // Ref for NOW ruler in list view (for auto-scroll)
  const nowRulerRef = useRef<HTMLDivElement>(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    location: '',
    category: 'meeting'
  });

  /**
   * Load events from backend
   */
  useEffect(() => {
    loadEvents();
  }, []);

  /**
   * Register real-time refresh function
   */
  useEffect(() => {
    registerCalendarRefresh(loadEvents);
  }, [registerCalendarRefresh]);

  /**
   * Auto-scroll to NOW ruler in list view
   */
  useEffect(() => {
    if (viewMode === 'list' && nowRulerRef.current) {
      setTimeout(() => {
        nowRulerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [viewMode]);

  /**
   * On mobile, default to 'day' view if in unsupported mode
   */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        // On mobile, switch to 'day' if in 'month' or 'week'
        if (viewMode === 'month' || viewMode === 'week') {
          setViewMode('day');
        }
      }
    };

    // Check on mount
    handleResize();

    // Listen for resize
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewMode]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/personal-data/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Create new event
   */
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.post('/personal-data/events', formData);
      await loadEvents();
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to create event:', error);
    }
  };

  /**
   * Update existing event
   */
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEvent) return;

    try {
      await apiClient.put(`/personal-data/events/${selectedEvent.id}`, formData);
      await loadEvents();
      setShowEditModal(false);
      setSelectedEvent(null);
      resetForm();
    } catch (error) {
      console.error('Failed to update event:', error);
    }
  };

  /**
   * Delete event
   */
  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this event?')) return;

    try {
      await apiClient.delete(`/personal-data/events/${id}`);
      await loadEvents();
      setSelectedEvent(null);
    } catch (error) {
      console.error('Failed to delete event:', error);
    }
  };

  /**
   * Format datetime for datetime-local input (YYYY-MM-DDTHH:mm)
   */
  const formatDateTimeLocal = (dateString: string): string => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  /**
   * Open detail modal (read-only view)
   */
  const openDetailModal = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setShowDetailModal(true);
  };

  /**
   * Open edit modal with event data (from detail modal)
   */
  const openEditModal = () => {
    if (!selectedEvent) return;

    setFormData({
      title: selectedEvent.title,
      description: selectedEvent.description,
      start_time: formatDateTimeLocal(selectedEvent.start_time),
      end_time: formatDateTimeLocal(selectedEvent.end_time),
      location: selectedEvent.location || '',
      category: selectedEvent.category || 'meeting'
    });
    setShowDetailModal(false);
    setShowEditModal(true);
  };

  /**
   * Reset form
   */
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      start_time: '',
      end_time: '',
      location: '',
      category: 'meeting'
    });
  };

  /**
   * Open create modal with pre-filled date/time
   */
  const openCreateModal = (date?: Date, hour?: number) => {
    const start = date ? new Date(date) : new Date();
    if (hour !== undefined) {
      start.setHours(hour, 0, 0, 0);
    }
    const end = new Date(start);
    end.setHours(start.getHours() + 1);

    setFormData({
      ...formData,
      start_time: start.toISOString().slice(0, 16),
      end_time: end.toISOString().slice(0, 16),
    });
    setShowCreateModal(true);
  };

  /**
   * Navigation helpers
   */
  const goToToday = () => setCurrentDate(new Date());

  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  /**
   * Get events for a specific day
   */
  const getEventsForDay = (date: Date): CalendarEvent[] => {
    return events.filter(event => {
      const eventStart = new Date(event.start_time);
      return eventStart.toDateString() === date.toDateString();
    });
  };


  /**
   * Calculate event position and height for time-based views
   */
  interface EventPosition {
    event: CalendarEvent;
    top: number; // pixels from top of day
    height: number; // height in pixels
    column: number; // which column (for overlaps)
    totalColumns: number; // total overlapping columns
  }

  const calculateEventPositions = (dayEvents: CalendarEvent[]): EventPosition[] => {
    const HOUR_HEIGHT = 60; // pixels per hour

    // Calculate basic position for each event
    const positioned = dayEvents.map(event => {
      const start = new Date(event.start_time);
      const end = new Date(event.end_time);

      // Calculate top position (minutes from midnight)
      const startMinutes = start.getHours() * 60 + start.getMinutes();
      const top = (startMinutes / 60) * HOUR_HEIGHT;

      // Calculate height (duration in minutes)
      const durationMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
      const height = Math.max((durationMinutes / 60) * HOUR_HEIGHT, 20); // Minimum 20px

      return {
        event,
        top,
        height,
        startTime: start.getTime(),
        endTime: end.getTime(),
        column: 0,
        totalColumns: 1
      };
    });

    // Sort by start time, then by duration (longer events first)
    positioned.sort((a, b) => {
      if (a.startTime !== b.startTime) {
        return a.startTime - b.startTime;
      }
      return (b.endTime - b.startTime) - (a.endTime - a.startTime);
    });

    // Detect overlaps and assign columns
    const columns: { start: number; end: number }[][] = [[]];

    positioned.forEach((pos) => {
      let placed = false;

      // Try to place in existing columns
      for (let col = 0; col < columns.length; col++) {
        const colEvents = columns[col];
        const hasOverlap = colEvents.some(existing =>
          !(pos.endTime <= existing.start || pos.startTime >= existing.end)
        );

        if (!hasOverlap) {
          colEvents.push({ start: pos.startTime, end: pos.endTime });
          pos.column = col;
          placed = true;
          break;
        }
      }

      // Create new column if needed
      if (!placed) {
        columns.push([{ start: pos.startTime, end: pos.endTime }]);
        pos.column = columns.length - 1;
      }

      // Find overlapping events to determine total columns
      const overlapping = positioned.filter(other =>
        !(pos.endTime <= other.startTime || pos.startTime >= other.endTime)
      );
      pos.totalColumns = Math.max(...overlapping.map(o => o.column + 1), 1);
    });

    return positioned;
  };

  /**
   * Generate month calendar grid
   */
  const generateMonthGrid = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay()); // Start from Sunday

    const weeks: Date[][] = [];
    let currentWeek: Date[] = [];

    for (let i = 0; i < 42; i++) { // 6 weeks max
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      currentWeek.push(date);

      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    return weeks;
  };

  /**
   * Generate week view days
   */
  const generateWeekDays = () => {
    const days: Date[] = [];
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay()); // Sunday

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }

    return days;
  };

  /**
   * Render Month View
   */
  const renderMonthView = () => {
    const weeks = generateMonthGrid();
    const today = new Date();

    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="py-2 text-center text-xs font-semibold text-gray-600">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {weeks.map((week, weekIdx) => (
            <React.Fragment key={weekIdx}>
              {week.map((date, dayIdx) => {
                const dayEvents = getEventsForDay(date);
                const isToday = date.toDateString() === today.toDateString();
                const isCurrentMonth = date.getMonth() === currentDate.getMonth();

                return (
                  <div
                    key={`${weekIdx}-${dayIdx}`}
                    className={`min-h-[100px] border-r border-b border-gray-200 p-1 cursor-pointer hover:bg-gray-50 transition-colors ${
                      !isCurrentMonth ? 'bg-gray-50/50' : 'bg-white'
                    }`}
                    onClick={() => openCreateModal(date)}
                  >
                    <div className={`text-xs font-medium mb-1 ${
                      isToday ? 'bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center' :
                      isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                    }`}>
                      {date.getDate()}
                    </div>

                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map(event => (
                        <div
                          key={event.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetailModal(event);
                          }}
                          className={`text-xs px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80 ${
                            event.category === 'meeting' ? 'bg-blue-100 text-blue-700' :
                            event.category === 'deadline' ? 'bg-red-100 text-red-700' :
                            event.category === 'reminder' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {new Date(event.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} {event.title}
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDayEvents({ date, events: dayEvents });
                            setShowDayEventsModal(true);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium px-1 underline cursor-pointer"
                        >
                          +{dayEvents.length - 3} more
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  /**
   * Render Week View
   */
  const renderWeekView = () => {
    const weekDays = generateWeekDays();
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const today = new Date();
    const HOUR_HEIGHT = 60;

    return (
      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <div className="flex min-w-[320px] md:min-w-[600px] lg:min-w-[800px]">
          {/* Time column */}
          <div className="flex-shrink-0 w-16 border-r border-gray-200">
            {/* Header spacer */}
            <div className="h-16 border-b border-gray-200 bg-gray-50"></div>

            {/* Time labels */}
            {hours.map(hour => (
              <div key={hour} className="relative" style={{ height: `${HOUR_HEIGHT}px` }}>
                <div className="absolute -top-2 right-2 text-xs text-gray-600 bg-white pr-1">
                  {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                </div>
              </div>
            ))}
          </div>

          {/* Days */}
          <div className="flex-1 grid grid-cols-7">
            {weekDays.map(day => {
              const isToday = day.toDateString() === today.toDateString();
              const dayEvents = getEventsForDay(day);
              const positionedEvents = calculateEventPositions(dayEvents);

              return (
                <div key={day.toISOString()} className="border-r border-gray-200 last:border-r-0">
                  {/* Day header */}
                  <div className="h-16 border-b border-gray-200 bg-gray-50 p-2 text-center sticky top-0 z-20">
                    <div className={`text-xs font-semibold ${isToday ? 'text-blue-600' : 'text-gray-600'}`}>
                      {day.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className={`text-lg font-bold ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                      {day.getDate()}
                    </div>
                  </div>

                  {/* Day grid and events */}
                  <div className="relative" style={{ height: `${24 * HOUR_HEIGHT}px` }}>
                    {/* Hour grid lines */}
                    {hours.map(hour => (
                      <div
                        key={hour}
                        className="absolute w-full border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
                        style={{
                          top: `${hour * HOUR_HEIGHT}px`,
                          height: `${HOUR_HEIGHT}px`
                        }}
                        onClick={() => openCreateModal(day, hour)}
                      />
                    ))}

                    {/* NOW bar - only show on today's column */}
                    {isToday && (() => {
                      const nowHour = today.getHours();
                      const nowMinutes = today.getMinutes();
                      const nowPosition = (nowHour + nowMinutes / 60) * HOUR_HEIGHT;

                      return (
                        <div
                          className="absolute w-full flex items-center z-30 pointer-events-none"
                          style={{ top: `${nowPosition}px` }}
                        >
                          <div className="w-2 h-2 bg-red-500 rounded-full -ml-1"></div>
                          <div className="flex-1 h-0.5 bg-red-500"></div>
                        </div>
                      );
                    })()}

                    {/* Events */}
                    {positionedEvents.map(({ event, top, height, column, totalColumns }) => {
                      const left = (column / totalColumns) * 100;
                      const width = (1 / totalColumns) * 100;

                      return (
                        <div
                          key={event.id}
                          className={`absolute px-1 py-1 rounded cursor-pointer hover:opacity-90 transition-opacity overflow-hidden ${
                            event.category === 'meeting' ? 'bg-blue-500 text-white border-l-4 border-blue-700' :
                            event.category === 'deadline' ? 'bg-red-500 text-white border-l-4 border-red-700' :
                            event.category === 'reminder' ? 'bg-yellow-500 text-white border-l-4 border-yellow-700' :
                            'bg-gray-500 text-white border-l-4 border-gray-700'
                          }`}
                          style={{
                            top: `${top}px`,
                            height: `${height}px`,
                            left: `${left}%`,
                            width: `${width - 1}%`, // -1% for spacing
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetailModal(event);
                          }}
                        >
                          <div className="text-xs font-semibold truncate">{event.title}</div>
                          <div className="text-xs opacity-90 truncate">
                            {new Date(event.start_time).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </div>
                          {event.location && height > 40 && (
                            <div className="text-xs opacity-75 truncate mt-0.5">{event.location}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  /**
   * Render Day View
   */
  const renderDayView = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const HOUR_HEIGHT = 80;
    const dayEvents = getEventsForDay(currentDate);
    const positionedEvents = calculateEventPositions(dayEvents);

    return (
      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <div className="min-w-[320px] md:min-w-[500px] lg:min-w-[600px]">
          {/* Day header */}
          <div className="border-b border-gray-200 bg-gray-50 p-4 text-center sticky top-0 z-20">
            <div className="text-sm font-semibold text-gray-600">
              {currentDate.toLocaleDateString('en-US', { weekday: 'long' })}
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {currentDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
          </div>

          {/* Day timeline */}
          <div className="flex">
            {/* Time column */}
            <div className="flex-shrink-0 w-24 border-r border-gray-200">
              {hours.map(hour => (
                <div key={hour} className="relative" style={{ height: `${HOUR_HEIGHT}px` }}>
                  <div className="absolute -top-2 right-2 text-sm text-gray-600 bg-white pr-1">
                    {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                  </div>
                </div>
              ))}
            </div>

            {/* Events column */}
            <div className="flex-1 relative" style={{ height: `${24 * HOUR_HEIGHT}px` }}>
              {/* Hour grid lines */}
              {hours.map(hour => (
                <div
                  key={hour}
                  className="absolute w-full border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
                  style={{
                    top: `${hour * HOUR_HEIGHT}px`,
                    height: `${HOUR_HEIGHT}px`
                  }}
                  onClick={() => openCreateModal(currentDate, hour)}
                />
              ))}

              {/* NOW bar - show if viewing today */}
              {(() => {
                const today = new Date();
                const isViewingToday = currentDate.toDateString() === today.toDateString();

                if (!isViewingToday) return null;

                const nowHour = today.getHours();
                const nowMinutes = today.getMinutes();
                const nowPosition = (nowHour + nowMinutes / 60) * HOUR_HEIGHT;

                return (
                  <div
                    className="absolute w-full flex items-center z-30 pointer-events-none"
                    style={{ top: `${nowPosition}px` }}
                  >
                    <div className="w-3 h-3 bg-red-500 rounded-full -ml-1.5"></div>
                    <div className="flex-1 h-0.5 bg-red-500"></div>
                    <div className="px-2 py-0.5 bg-red-500 text-white text-xs font-semibold rounded ml-2">
                      {today.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </div>
                  </div>
                );
              })()}

              {/* Events */}
              {positionedEvents.map(({ event, top, height, column, totalColumns }) => {
                const left = (column / totalColumns) * 100;
                const width = (1 / totalColumns) * 100;
                // Adjust height for day view's larger hour height
                const adjustedTop = (top / 60) * HOUR_HEIGHT;
                const adjustedHeight = Math.max((height / 60) * HOUR_HEIGHT, 30);

                return (
                  <div
                    key={event.id}
                    className={`absolute px-3 py-2 rounded cursor-pointer hover:opacity-90 transition-opacity overflow-hidden shadow-md ${
                      event.category === 'meeting' ? 'bg-blue-500 text-white border-l-4 border-blue-700' :
                      event.category === 'deadline' ? 'bg-red-500 text-white border-l-4 border-red-700' :
                      event.category === 'reminder' ? 'bg-yellow-500 text-white border-l-4 border-yellow-700' :
                      'bg-gray-500 text-white border-l-4 border-gray-700'
                    }`}
                    style={{
                      top: `${adjustedTop}px`,
                      height: `${adjustedHeight}px`,
                      left: `${left}%`,
                      width: `${width - 1}%`, // -1% for spacing
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      openDetailModal(event);
                    }}
                  >
                    <div className="font-semibold text-sm truncate">{event.title}</div>
                    <div className="text-xs opacity-90 mt-1">
                      {new Date(event.start_time).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit'
                      })} - {new Date(event.end_time).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </div>
                    {event.location && adjustedHeight > 60 && (
                      <div className="text-xs opacity-75 mt-1 truncate">📍 {event.location}</div>
                    )}
                    {event.description && adjustedHeight > 90 && (
                      <div className="text-xs opacity-75 mt-1 line-clamp-2">{event.description}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Render List View - Grouped by day with NOW ruler
   */
  const renderListView = () => {
    // Sort events from oldest to newest
    const sortedEvents = [...events].sort((a, b) =>
      new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );

    if (sortedEvents.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No events found</h3>
          <p className="text-gray-500 mb-4">Get started by creating your first event</p>
          <button onClick={() => openCreateModal()} className="btn-primary">
            Create Event
          </button>
        </div>
      );
    }

    // Group events by day
    const eventsByDay = sortedEvents.reduce((groups, event) => {
      const date = new Date(event.start_time);
      const dateKey = date.toDateString(); // "Mon Jan 01 2024"
      if (!groups[dateKey]) {
        groups[dateKey] = {
          date: date,
          events: []
        };
      }
      groups[dateKey].events.push(event);
      return groups;
    }, {} as Record<string, { date: Date; events: CalendarEvent[] }>);

    const groupedDays = Object.values(eventsByDay);
    const now = new Date();
    const nowTime = now.getTime();

    // Find where to place NOW ruler based on actual event times
    // Structure: { dayIndex: number, eventIndex: number }
    // eventIndex: -1 means before all events in that day
    let nowRulerPosition: { dayIndex: number; eventIndex: number } | null = null;

    if (sortedEvents.length === 0) {
      // No events, no ruler
      nowRulerPosition = null;
    } else {
      // Check if all events are in the past
      const lastEventTime = new Date(sortedEvents[sortedEvents.length - 1].end_time).getTime();
      if (nowTime > lastEventTime) {
        nowRulerPosition = null; // Don't show (all past)
      } else {
        // Find which day contains NOW or should contain the ruler
        let found = false;

        for (let dayIdx = 0; dayIdx < groupedDays.length; dayIdx++) {
          const dayGroup = groupedDays[dayIdx];
          const dayDate = dayGroup.date;
          const dayEvents = dayGroup.events;

          // Calculate day boundaries for comparison
          const dayStart = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate()).getTime();
          const dayEnd = dayStart + 24 * 60 * 60 * 1000;

          // If NOW is before this day starts, place ruler before this day
          if (nowTime < dayStart) {
            nowRulerPosition = { dayIndex: dayIdx - 1, eventIndex: -1 };
            if (dayIdx === 0) {
              // First day and NOW is before it
              nowRulerPosition = { dayIndex: -1, eventIndex: -1 };
            }
            found = true;
            break;
          }

          // If NOW is within this day (same calendar day)
          if (nowTime >= dayStart && nowTime < dayEnd) {
            // Find position within this day's events
            let positionFound = false;

            for (let eventIdx = 0; eventIdx < dayEvents.length; eventIdx++) {
              const event = dayEvents[eventIdx];
              const eventStart = new Date(event.start_time).getTime();
              const eventEnd = new Date(event.end_time).getTime();

              // If NOW is before this event starts
              if (nowTime < eventStart) {
                nowRulerPosition = { dayIndex: dayIdx, eventIndex: eventIdx - 1 };
                positionFound = true;
                break;
              }

              // If NOW is during this event, place ruler after it
              if (nowTime >= eventStart && nowTime <= eventEnd) {
                nowRulerPosition = { dayIndex: dayIdx, eventIndex: eventIdx };
                positionFound = true;
                break;
              }
            }

            // If not found within events but we're in this day, it means NOW is after all events today
            if (!positionFound) {
              nowRulerPosition = { dayIndex: dayIdx, eventIndex: dayEvents.length - 1 };
            }

            found = true;
            break;
          }
        }

        // If we went through all days and didn't find a position, NOW might be between days
        if (!found && groupedDays.length > 0) {
          // Check if NOW is between the last event of one day and first event of next day
          for (let dayIdx = 0; dayIdx < groupedDays.length - 1; dayIdx++) {
            const lastEventOfDay = groupedDays[dayIdx].events[groupedDays[dayIdx].events.length - 1];
            const lastEventEnd = new Date(lastEventOfDay.end_time).getTime();
            const firstEventOfNextDay = groupedDays[dayIdx + 1].events[0];
            const firstEventStart = new Date(firstEventOfNextDay.start_time).getTime();

            if (nowTime > lastEventEnd && nowTime < firstEventStart) {
              nowRulerPosition = { dayIndex: dayIdx, eventIndex: groupedDays[dayIdx].events.length - 1 };
              break;
            }
          }
        }
      }
    }

    return (
      <div className="space-y-8 max-w-4xl mx-auto">
        {/* NOW ruler at the beginning if all events are in the future */}
        {nowRulerPosition?.dayIndex === -1 && (
          <div ref={nowRulerRef} className="flex items-center gap-4 my-6">
            <div className="flex-1 h-0.5 bg-red-500"></div>
            <div className="flex items-center gap-2 px-3 py-1 bg-red-500 text-white rounded-full text-sm font-semibold">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              NOW - {now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </div>
            <div className="flex-1 h-0.5 bg-red-500"></div>
          </div>
        )}

        {groupedDays.map((dayGroup, dayIndex) => {
          const { date, events: dayEvents } = dayGroup;
          const isToday = date.toDateString() === now.toDateString();
          const isPast = date < now && !isToday;

          return (
            <React.Fragment key={date.toISOString()}>
              {/* Day Header */}
              <div className="sticky top-0 z-10 bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`text-lg font-semibold ${isToday ? 'text-blue-600' : isPast ? 'text-gray-600' : 'text-gray-900'}`}>
                      {isToday ? 'Today' : date.toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
                      })}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  {isToday && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                      Current Day
                    </span>
                  )}
                </div>
              </div>

              {/* Events for this day */}
              <div className="space-y-3 pl-6">
                {/* NOW ruler before first event if needed */}
                {nowRulerPosition?.dayIndex === dayIndex && nowRulerPosition.eventIndex === -1 && (
                  <div ref={nowRulerRef} className="flex items-center gap-4 my-6">
                    <div className="flex-1 h-0.5 bg-red-500"></div>
                    <div className="flex items-center gap-2 px-3 py-1 bg-red-500 text-white rounded-full text-sm font-semibold">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      NOW - {now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </div>
                    <div className="flex-1 h-0.5 bg-red-500"></div>
                  </div>
                )}

                {dayEvents.map((event, eventIndex) => {
                  const eventStart = new Date(event.start_time);
                  const eventEnd = new Date(event.end_time);
                  const eventPassed = eventEnd < now;

                  return (
                    <React.Fragment key={event.id}>
                      <div
                        className={`bg-white rounded-lg shadow-sm border hover:shadow-md transition-all cursor-pointer ${
                          eventPassed ? 'border-gray-200 opacity-75' : 'border-gray-300'
                        }`}
                        onClick={() => openDetailModal(event)}
                      >
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-start gap-3 flex-1">
                              {/* Time badge */}
                              <div className={`text-center flex-shrink-0 ${eventPassed ? 'text-gray-400' : 'text-blue-600'}`}>
                                <div className="text-sm font-semibold">
                                  {eventStart.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                </div>
                                <div className="text-xs opacity-75">
                                  {eventEnd.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                </div>
                              </div>

                              {/* Event details */}
                              <div className="flex-1 min-w-0">
                                <h4 className={`font-semibold mb-1 ${eventPassed ? 'text-gray-600' : 'text-gray-900'}`}>
                                  {event.title}
                                </h4>
                                {event.description && (
                                  <p className={`text-sm mb-2 line-clamp-2 ${eventPassed ? 'text-gray-500' : 'text-gray-600'}`}>
                                    {event.description}
                                  </p>
                                )}
                                {event.location && (
                                  <div className={`flex items-center gap-1 text-sm ${eventPassed ? 'text-gray-400' : 'text-gray-500'}`}>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <span className="truncate">{event.location}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Category badge */}
                            {event.category && (
                              <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ml-2 ${
                                event.category === 'meeting' ? 'bg-blue-100 text-blue-700' :
                                event.category === 'deadline' ? 'bg-red-100 text-red-700' :
                                event.category === 'reminder' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {event.category.charAt(0).toUpperCase() + event.category.slice(1)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* NOW ruler after this event if needed */}
                      {nowRulerPosition?.dayIndex === dayIndex && nowRulerPosition.eventIndex === eventIndex && (
                        <div ref={nowRulerRef} className="flex items-center gap-4 my-6">
                          <div className="flex-1 h-0.5 bg-red-500"></div>
                          <div className="flex items-center gap-2 px-3 py-1 bg-red-500 text-white rounded-full text-sm font-semibold">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            NOW - {now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          </div>
                          <div className="flex-1 h-0.5 bg-red-500"></div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  /**
   * Get current period label
   */
  const getPeriodLabel = () => {
    if (viewMode === 'month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else if (viewMode === 'week') {
      const weekDays = generateWeekDays();
      return `${weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else if (viewMode === 'day') {
      return currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
    return 'All Events';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between gap-4 mb-4">
            {/* Back button + Title */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/voice')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Back to voice interface"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Calendar</h1>
                <p className="text-xs md:text-sm text-gray-500">{events.length} events</p>
              </div>
            </div>

            {/* Actions */}
            <button
              onClick={() => openCreateModal()}
              className="btn-primary text-sm px-3 md:px-4 py-2 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">New Event</span>
              <span className="sm:hidden">New</span>
            </button>
          </div>

          {/* Navigation and View Mode */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Period navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={goToToday}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Today
              </button>
              <button
                onClick={goToPrevious}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={goToNext}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <span className="text-sm font-medium text-gray-900 ml-2">{getPeriodLabel()}</span>
            </div>

            {/* View Mode Selector - Mobile shows only 'day' and 'list' */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              {(['month', 'week', 'day', 'list'] as ViewMode[]).map((mode) => {
                // On mobile (< 768px), only show 'day' and 'list'
                const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
                if (isMobile && mode !== 'day' && mode !== 'list') {
                  return null;
                }

                return (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={`px-3 py-1.5 rounded text-xs md:text-sm font-medium transition-colors ${
                      viewMode === mode
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
          </div>
        ) : (
          <>
            {viewMode === 'month' && renderMonthView()}
            {viewMode === 'week' && renderWeekView()}
            {viewMode === 'day' && renderDayView()}
            {viewMode === 'list' && renderListView()}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Create New Event</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Event title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Event description"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Time *</label>
                  <input
                    type="datetime-local"
                    value={formData.start_time}
                    onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Time *</label>
                  <input
                    type="datetime-local"
                    value={formData.end_time}
                    onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Event location"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="meeting">Meeting</option>
                  <option value="deadline">Deadline</option>
                  <option value="reminder">Reminder</option>
                  <option value="personal">Personal</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-primary flex-1">
                  Create Event
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Event Detail Modal (Read-only) */}
      {showDetailModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">{selectedEvent.title}</h2>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedEvent(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Category Badge */}
              {selectedEvent.category && (
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                    selectedEvent.category === 'meeting' ? 'bg-blue-100 text-blue-700' :
                    selectedEvent.category === 'deadline' ? 'bg-red-100 text-red-700' :
                    selectedEvent.category === 'reminder' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {selectedEvent.category.charAt(0).toUpperCase() + selectedEvent.category.slice(1)}
                  </span>
                </div>
              )}

              {/* Time */}
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <div className="font-medium text-gray-900">
                      {new Date(selectedEvent.start_time).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {new Date(selectedEvent.start_time).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit'
                      })} - {new Date(selectedEvent.end_time).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Location */}
              {selectedEvent.location && (
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <div className="text-sm text-gray-900">{selectedEvent.location}</div>
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedEvent.description && (
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                  <div>
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">{selectedEvent.description}</div>
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="pt-4 border-t border-gray-200">
                <div className="text-xs text-gray-500 space-y-1">
                  <div>Created: {new Date(selectedEvent.created_at).toLocaleString()}</div>
                  <div>Last updated: {new Date(selectedEvent.updated_at).toLocaleString()}</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={openEditModal}
                  className="btn-primary flex-1"
                >
                  Edit Event
                </button>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    handleDelete(selectedEvent.id);
                  }}
                  className="btn-secondary flex-1 !bg-red-50 !text-red-600 hover:!bg-red-100"
                >
                  Delete Event
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Edit Event</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedEvent(null);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Time *</label>
                  <input
                    type="datetime-local"
                    value={formData.start_time}
                    onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Time *</label>
                  <input
                    type="datetime-local"
                    value={formData.end_time}
                    onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="meeting">Meeting</option>
                  <option value="deadline">Deadline</option>
                  <option value="reminder">Reminder</option>
                  <option value="personal">Personal</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-primary flex-1">
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedEvent(null);
                    resetForm();
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Day Events Modal (shows all events for a specific day) */}
      {showDayEventsModal && selectedDayEvents && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {selectedDayEvents.date.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </h2>
              <button
                onClick={() => {
                  setShowDayEventsModal(false);
                  setSelectedDayEvents(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div className="text-sm text-gray-600 mb-4">
                {selectedDayEvents.events.length} event{selectedDayEvents.events.length !== 1 ? 's' : ''}
              </div>

              <div className="space-y-3">
                {selectedDayEvents.events
                  .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
                  .map(event => (
                    <div
                      key={event.id}
                      onClick={() => {
                        setShowDayEventsModal(false);
                        openDetailModal(event);
                      }}
                      className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-900 flex-1">{event.title}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ml-2 ${
                          event.category === 'meeting' ? 'bg-blue-100 text-blue-700' :
                          event.category === 'deadline' ? 'bg-red-100 text-red-700' :
                          event.category === 'reminder' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {event.category || 'other'}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>
                          {new Date(event.start_time).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit'
                          })} - {new Date(event.end_time).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>

                      {event.location && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>{event.location}</span>
                        </div>
                      )}

                      {event.description && (
                        <p className="text-sm text-gray-600 line-clamp-2">{event.description}</p>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarModule;
